
import { useSyncExternalStore } from "react";

import { SIDEBAR_ATTRIBUTE, SIDEBAR_STORAGE_KEY, THEME_STORAGE_KEY } from "./boot-script";

export type ThemePreference = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";
export type SidebarPreference = "expanded" | "rail" | null;

const CHANGE_EVENT = "admin-preferences-change";
const THEME_CHANGING_ATTRIBUTE = "data-theme-changing";

let systemSchemeWatched = false;

function holdTransitions() {
  const root = document.documentElement;
  root.setAttribute(THEME_CHANGING_ATTRIBUTE, "");
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.removeAttribute(THEME_CHANGING_ATTRIBUTE));
  });
}

function watchSystemScheme() {
  if (systemSchemeWatched) return;
  systemSchemeWatched = true;
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", holdTransitions);
}

function emitChange() {
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

function subscribe(onChange: () => void) {
  const onStorage = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY) applyTheme(readStoredTheme());
    if (event.key === SIDEBAR_STORAGE_KEY) applySidebar(readStoredSidebar());
    if (event.key === THEME_STORAGE_KEY || event.key === SIDEBAR_STORAGE_KEY || event.key === null) onChange();
  };
  const scheme = window.matchMedia("(prefers-color-scheme: dark)");
  watchSystemScheme();
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener("storage", onStorage);
  scheme.addEventListener("change", onChange);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener("storage", onStorage);
    scheme.removeEventListener("change", onChange);
  };
}

function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {}
}

function readStoredTheme(): ThemePreference {
  const value = safeRead(THEME_STORAGE_KEY);
  return value === "light" || value === "dark" ? value : "system";
}

function readStoredSidebar(): SidebarPreference {
  const value = safeRead(SIDEBAR_STORAGE_KEY);
  return value === "expanded" || value === "rail" ? value : null;
}

function applyTheme(preference: ThemePreference) {
  const root = document.documentElement;
  holdTransitions();
  if (preference === "system") {
    root.removeAttribute("data-theme");
    root.style.removeProperty("color-scheme");
  } else {
    root.setAttribute("data-theme", preference);
    root.style.colorScheme = preference;
  }
}

function applySidebar(preference: SidebarPreference) {
  const root = document.documentElement;
  if (preference) root.setAttribute(SIDEBAR_ATTRIBUTE, preference);
  else root.removeAttribute(SIDEBAR_ATTRIBUTE);
}

function themeSnapshot(): string {
  const preference = readStoredTheme();
  const resolved = preference === "system"
    ? (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
    : preference;
  return `${preference}:${resolved}`;
}

const themeServerSnapshot = () => "system:light";

export function useTheme(): {
  preference: ThemePreference;
  resolved: ResolvedTheme;
  setPreference: (preference: ThemePreference) => void;
} {
  const [preference, resolved] = useSyncExternalStore(subscribe, themeSnapshot, themeServerSnapshot).split(":") as [
    ThemePreference,
    ResolvedTheme,
  ];
  return { preference, resolved, setPreference: setThemePreference };
}

export function setThemePreference(preference: ThemePreference) {
  safeWrite(THEME_STORAGE_KEY, preference === "system" ? null : preference);
  applyTheme(preference);
  emitChange();
}

const sidebarServerSnapshot = () => null;

export function useSidebarPreference(): SidebarPreference {
  return useSyncExternalStore(subscribe, readStoredSidebar, sidebarServerSnapshot);
}

export function setSidebarPreference(preference: SidebarPreference) {
  safeWrite(SIDEBAR_STORAGE_KEY, preference);
  applySidebar(preference);
  emitChange();
}
